import { queryOptions } from '@tanstack/react-query';
import { registerBaseURL } from '#constants/hosts';
import { client as qontoClient } from '#queries/utils';
import { persons as kycAcceptance } from '#proto/index.qonto.kycb.acceptance';
import { verification as identityVerification } from '#proto/index.qonto.identity_verification';
import {
  ApiCreateKYCRequest,
  ApiCreateKYCResponse,
} from '#proto/qonto/registration/kyc/firstparty/v2/create_kyc';
import { ApiCreateFourthlineProcessResponse } from '#proto/index.qonto.kycb.api.identities.firstparty.v1';
import type { JSONApiDataEntity } from '../../../src/types/json-api';
import type {
  FourthlineProcessAttributes,
  FourthlineProcessResponse,
  GetIdentityVerificationResponse,
  KycResponse,
  RequestCredentialsResponse,
} from './types';
import { isFourthlineEnvelope } from './utils';

interface KycQueriesItemParams {
  /** Market identifier (e.g. legal country) for cache isolation and the `market` query parameter. */
  market?: string;
  /**
   * Organization ID for the `X-Qonto-Organization-Id` header.
   * Only needed when session storage is not yet populated (e.g. during login).
   * Intentionally excluded from query keys — used for auth only, not cache differentiation.
   */
  organizationId?: string;
}

/** Query key factory for KYC acceptance queries. */
export const kycQueryKeys = {
  all: ['kyc-acceptance'] as const,
  /** Query key for a single person's KYC status. Only {@link KycQueriesItemParams.market} affects the key. */
  item: (params: KycQueriesItemParams = {}) =>
    [...kycQueryKeys.all, 'status', ...(params.market ? [params.market] : [])] as const,
  /** Query key for creating (or retrieving) a stakeholder's KYC process. */
  create: (stakeholderId: string) => [...kycQueryKeys.all, 'create', stakeholderId] as const,
  fourthlineProcess: (identityId: string) =>
    [...kycQueryKeys.all, 'fourthline-process', identityId] as const,
  /** Query key for a single identity verification's status, keyed by its ID. */
  identityVerification: (identityVerificationId: string) =>
    [...kycQueryKeys.all, 'identity-verification', identityVerificationId] as const,
  /** Query key for requesting (or retrieving) an identity verification's credentials. */
  credentials: (identityVerificationId: string) =>
    [...kycQueryKeys.all, 'credentials', identityVerificationId] as const,
};

async function createRegisterProcess(stakeholderId: string): Promise<ApiCreateKYCResponse> {
  const requestBody = ApiCreateKYCRequest.toJSON({ stakeholderId });

  const response = await qontoClient(`${registerBaseURL}/v2/kyc`, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  if (isFourthlineEnvelope(response)) {
    return ApiCreateKYCResponse.fromJSON({ fourthlineProcess: response.data.attributes });
  }

  return ApiCreateKYCResponse.fromJSON(response);
}

async function fetchFourthlineProcess(identityId: string): Promise<FourthlineProcessResponse> {
  const response = await qontoClient<{
    data: JSONApiDataEntity<FourthlineProcessAttributes>;
  }>(`/v1/identities/${identityId}/fourthline_process`, {
    method: 'PUT',
  });

  return ApiCreateFourthlineProcessResponse.fromJSON({
    id: response.data.id,
    ...response.data.attributes,
  });
}

async function fetchIdentityVerification(
  identityVerificationId: string
): Promise<GetIdentityVerificationResponse> {
  const response = await qontoClient(`/v4/identity_verifications/${identityVerificationId}`, {
    method: 'GET',
  });

  return identityVerification.firstparty.v4.ApiGetIdentityVerificationResponse.fromJSON(response);
}

async function fetchCredentials(
  identityVerificationId: string
): Promise<RequestCredentialsResponse> {
  const response = await qontoClient(
    `/v4/identity_verifications/${identityVerificationId}/credentials`,
    { method: 'POST' }
  );

  return identityVerification.firstparty.v4.ApiRequestCredentialsResponse.fromJSON(response);
}

export async function fetchKycAcceptance({
  market,
  organizationId,
}: KycQueriesItemParams = {}): Promise<KycResponse> {
  const response = await qontoClient<KycResponse>(`/kycb-acceptance/v1/persons/me`, {
    method: 'GET',
    ...(market ? { params: { market } } : {}),
    ...(organizationId?.trim() ? { headers: { 'X-Qonto-Organization-Id': organizationId } } : {}),
  });

  return kycAcceptance.firstparty.v1.ApiGetPersonAcceptanceResponse.fromJSON(response);
}

/** Query option factories for KYC acceptance data. */
export const kycQueries = {
  all: kycQueryKeys.all,
  /**
   * Query options for the current user's KYC acceptance status
   * via `GET /kycb-acceptance/v1/persons/me`.
   *
   * @see {@link KycQueriesItemParams} for parameter details.
   */
  item: (params: KycQueriesItemParams = {}) =>
    queryOptions({
      queryKey: kycQueryKeys.item(params),
      queryFn: () => fetchKycAcceptance(params),
    }),
  /**
   * Query options that create (or retrieve) the KYC process for a stakeholder
   * via `POST {registerBaseURL}/v2/kyc`.
   *
   * Modelled as a query because the response drives what the KYC route renders.
   * `staleTime: Infinity` prevents re-POSTing on refocus/remount.
   */
  createRegisterProcess: (stakeholderId: string) =>
    queryOptions({
      queryKey: kycQueryKeys.create(stakeholderId),
      queryFn: () => createRegisterProcess(stakeholderId),
      staleTime: Infinity,
    }),
  /**
   * @deprecated Remove when BE adds `providerDetails` to the acceptance API.
   */
  fourthlineProcess: (identityId: string) =>
    queryOptions({
      queryKey: kycQueryKeys.fourthlineProcess(identityId),
      queryFn: () => fetchFourthlineProcess(identityId),
      staleTime: Infinity,
    }),
  /**
   * Query options for a single identity verification's status
   * via `GET /v4/identity_verifications/:id`.
   *
   * Callers that poll for status updates should set their own `refetchInterval`.
   */
  identityVerification: (identityVerificationId: string) =>
    queryOptions({
      queryKey: kycQueryKeys.identityVerification(identityVerificationId),
      queryFn: () => fetchIdentityVerification(identityVerificationId),
    }),
  /**
   * Query options that request (or retrieve) an identity verification's provider credentials
   * via `POST /v4/identity_verifications/:id/credentials`.
   *
   * Modelled as a query, not a mutation, so `staleTime: Infinity` prevents re-POSTing on
   * refocus/remount/StrictMode double-invoke.
   */
  credentials: (identityVerificationId: string) =>
    queryOptions({
      queryKey: kycQueryKeys.credentials(identityVerificationId),
      queryFn: () => fetchCredentials(identityVerificationId),
      staleTime: Infinity,
    }),
};
